import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Table,
  Tag,
  Button,
  Spin,
  Typography,
  Space,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import api from "../../../services/api";
import AppleInAppPayments from "../../../components/subscription/AppleInAppPayments";

const { Title, Text } = Typography;

const PERIOD_LABELS = {
  ONE_WEEK: "1 Week",
  ONE_MONTH: "1 Month",
  TWO_MONTHS: "2 Months",
  THREE_MONTHS: "3 Months",
  SIX_MONTHS: "6 Months",
  ONE_YEAR: "1 Year",
};

const OFFER_MODE_LABELS = {
  FREE_TRIAL: "Free Trial",
  PAY_AS_YOU_GO: "Pay As You Go",
  PAY_UP_FRONT: "Pay Up Front",
};

const humanizePeriod = (value) =>
  PERIOD_LABELS[String(value || "").toUpperCase()] || value || "-";

const humanizeOfferMode = (value) =>
  OFFER_MODE_LABELS[String(value || "").toUpperCase()] || value || "-";

const stateColor = (state) => {
  const value = String(state || "").toUpperCase();
  if (value === "APPROVED") {
    return "green";
  }
  if (["DEVELOPER_REMOVED_FROM_SALE", "REMOVED_FROM_SALE", "REJECTED"].includes(value)) {
    return "red";
  }
  return "orange";
};

const priceColumns = [
  {
    title: "Country",
    dataIndex: "territory",
    key: "territory",
    sorter: (a, b) => String(a.territory || "").localeCompare(String(b.territory || "")),
    render: (value) => value || "-",
  },
  {
    title: "Currency",
    dataIndex: "currency",
    key: "currency",
    render: (value) => value || "-",
  },
  {
    title: "Customer Pays",
    dataIndex: "customerPrice",
    key: "customerPrice",
    render: (value) => (value !== null && value !== undefined ? value : "-"),
  },
  {
    title: "You Receive",
    dataIndex: "proceeds",
    key: "proceeds",
    render: (value) => (value !== null && value !== undefined ? value : "-"),
  },
  {
    title: "Start Date",
    dataIndex: "startDate",
    key: "startDate",
    render: (value) => value || "Current",
  },
];

const introOfferColumns = [
  {
    title: "Country",
    dataIndex: "territory",
    key: "territory",
    render: (value) => value || "All",
  },
  {
    title: "Type",
    dataIndex: "offerMode",
    key: "offerMode",
    render: (value) => humanizeOfferMode(value),
  },
  {
    title: "Duration",
    dataIndex: "duration",
    key: "duration",
    render: (value) => humanizePeriod(value),
  },
  {
    title: "Periods",
    dataIndex: "numberOfPeriods",
    key: "numberOfPeriods",
    render: (value) => value ?? "-",
  },
  {
    title: "Price",
    key: "price",
    render: (_, record) => {
      if (String(record.offerMode || "").toUpperCase() === "FREE_TRIAL") {
        return "Free";
      }
      if (record.customerPrice === null || record.customerPrice === undefined) {
        return "-";
      }
      return `${record.customerPrice} ${record.currency || ""}`.trim();
    },
  },
  {
    title: "Start",
    dataIndex: "startDate",
    key: "startDate",
    render: (value) => value || "-",
  },
  {
    title: "End",
    dataIndex: "endDate",
    key: "endDate",
    render: (value) => value || "-",
  },
];

const promoOfferColumns = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (value) => value || "-",
  },
  {
    title: "Offer Code",
    dataIndex: "offerCode",
    key: "offerCode",
    render: (value) => value || "-",
  },
  {
    title: "Type",
    dataIndex: "offerMode",
    key: "offerMode",
    render: (value) => humanizeOfferMode(value),
  },
  {
    title: "Duration",
    dataIndex: "duration",
    key: "duration",
    render: (value) => humanizePeriod(value),
  },
  {
    title: "Periods",
    dataIndex: "numberOfPeriods",
    key: "numberOfPeriods",
    render: (value) => value ?? "-",
  },
];

const localizationColumns = [
  {
    title: "Locale",
    dataIndex: "locale",
    key: "locale",
    render: (value) => value || "-",
  },
  {
    title: "Display Name",
    dataIndex: "name",
    key: "name",
    render: (value) => value || "-",
  },
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    render: (value) => value || "-",
  },
];

function AppleInAppSubscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        setError("");
        // Aggregates several App Store Connect calls server-side; allow extra time.
        const response = await api.get(
          `/admin/app-store/subscriptions/${id}/details`,
          { timeout: 60000 }
        );
        setDetails(response?.data || null);
      } catch (err) {
        console.error("Error fetching App Store subscription details:", err);
        const errMsg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to load subscription details";
        setError(errMsg);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  const subscription = details?.subscription || {};

  return (
    <div>
      <Space style={{ marginBottom: "16px" }} align="center">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/apple-in-app")}>
          Back
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {subscription.productId || "Subscription Details"}
        </Title>
        {subscription.state ? (
          <Tag color={stateColor(subscription.state)}>{subscription.state}</Tag>
        ) : null}
      </Space>

      {error ? (
        <Card style={{ marginBottom: "24px" }}>
          <Text type="danger">{error}</Text>
        </Card>
      ) : null}

      <Card title="Overview" style={{ marginBottom: "24px" }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 3 }}>
          <Descriptions.Item label="Product ID">
            {subscription.productId || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Reference Name">
            {subscription.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {subscription.state ? (
              <Tag color={stateColor(subscription.state)}>{subscription.state}</Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Billing Period">
            {humanizePeriod(subscription.subscriptionPeriod)}
          </Descriptions.Item>
          <Descriptions.Item label="Family Shareable">
            {subscription.familySharable ? "Yes" : "No"}
          </Descriptions.Item>
          <Descriptions.Item label="Group Level">
            {subscription.groupLevel ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Apple Resource ID">
            {subscription.id || id}
          </Descriptions.Item>
          <Descriptions.Item label="Review Note" span={2}>
            {subscription.reviewNote || "-"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Store Listing (per language)" style={{ marginBottom: "24px" }}>
        <Table
          columns={localizationColumns}
          dataSource={details?.localizations || []}
          rowKey={(record, index) => record.locale || `locale-${index}`}
          pagination={false}
          size="small"
        />
      </Card>

      <Card title="Prices (per country)" style={{ marginBottom: "24px" }}>
        <Table
          columns={priceColumns}
          dataSource={details?.prices || []}
          rowKey={(record, index) => `${record.territory || "territory"}-${index}`}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          scroll={{ x: 700 }}
        />
      </Card>

      <Card title="Introductory Offers (free trial / intro price)" style={{ marginBottom: "24px" }}>
        <Table
          columns={introOfferColumns}
          dataSource={details?.introductoryOffers || []}
          rowKey={(record, index) => `${record.territory || "intro"}-${index}`}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>

      <Card title="Promotional Offers" style={{ marginBottom: "24px" }}>
        <Table
          columns={promoOfferColumns}
          dataSource={details?.promotionalOffers || []}
          rowKey={(record, index) => record.id || `promo-${index}`}
          pagination={false}
          size="small"
        />
      </Card>

      {subscription.productId ? (
        <AppleInAppPayments
          title={`Transaction History — ${subscription.productId}`}
          productId={subscription.productId}
          pageSize={20}
        />
      ) : null}
    </div>
  );
}

export default AppleInAppSubscriptionDetail;
